import type { SupplierCapability } from '@tandemone/types'
import { CAPABILITY_TAXONOMY, LEAF_INDEX } from '@tandemone/types'

import { Badge } from '@/components/ui/badge'

import { ProfileEmpty } from './profile-empty'
import { ProfileSection } from './profile-section'

/** a colored dot per top-level category, taxonomy order. */
const CATEGORY_DOT_CLASSES: Record<string, string> = {
  metal_removal_forming: 'bg-indigo-400',
  casting_molding: 'bg-sky-400',
  joining_assembly_electronics: 'bg-rose-400',
  additive_manufacturing: 'bg-teal-400',
  surface_treatment_finishing: 'bg-orange-400',
  sealing_gaskets_insulation: 'bg-violet-400',
  inspection_tooling_supply: 'bg-stone-400'
}

/** new taxonomy render: leaves grouped category -> group. */
export function CapabilityProfileOperations({
  companyName,
  rows
}: {
  companyName: string
  rows: SupplierCapability[]
}) {
  const filtered = rows.filter(
    row =>
      row.capabilityItemKey !== 'none_of_the_above' &&
      LEAF_INDEX[row.capabilityItemKey]
  )
  const byGroup = new Map<string, SupplierCapability[]>()
  for (const row of filtered) {
    const info = LEAF_INDEX[row.capabilityItemKey]!
    const list = byGroup.get(info.groupKey) ?? []
    list.push(row)
    byGroup.set(info.groupKey, list)
  }

  return (
    <ProfileSection title={`${companyName} Operations & Capabilities`}>
      {filtered.length === 0 ? (
        <ProfileEmpty message='No capabilities listed.' />
      ) : (
        <div className='flex flex-col gap-5'>
          {CAPABILITY_TAXONOMY.filter(category =>
            category.groups.some(group => byGroup.has(group.key))
          ).map(category => (
            <div key={category.key} className='space-y-2'>
              <div className='flex items-center gap-2'>
                <span
                  className={`inline-block size-2 rounded-full ${
                    CATEGORY_DOT_CLASSES[category.key] ?? 'bg-stone-400'
                  }`}
                />
                <h3 className='text-xs uppercase tracking-wide text-muted-foreground'>
                  {category.label}
                </h3>
              </div>
              <div className='flex flex-col gap-3'>
                {category.groups
                  .filter(group => byGroup.has(group.key))
                  .map(group => (
                    <div key={group.key} className='space-y-1'>
                      <p className='text-[11px] font-medium text-ink-soft'>
                        {group.label}
                      </p>
                      <div className='flex flex-col divide-y divide-border'>
                        {byGroup
                          .get(group.key)!
                          .slice()
                          .sort(
                            (left, right) =>
                              right.yearsInBusiness - left.yearsInBusiness
                          )
                          .map(row => (
                            <div
                              key={row.id}
                              className='flex flex-col gap-1.5 py-1.5 sm:grid sm:grid-cols-[1fr_auto_auto] sm:items-center sm:gap-0'
                            >
                              <div className='flex items-center justify-between gap-2 sm:contents'>
                                <span className='text-sm text-foreground sm:order-1'>
                                  {LEAF_INDEX[row.capabilityItemKey]!.itemLabel}
                                </span>
                                <span className='shrink-0 text-xs tabular-nums text-ink-soft sm:order-3 sm:pl-4 sm:text-right'>
                                  {row.yearsInBusiness} yrs
                                </span>
                              </div>
                              <span className='flex flex-wrap items-center gap-2 sm:order-2 sm:justify-end sm:pl-3'>
                                <Badge
                                  variant='outline'
                                  className={`w-28 justify-center ${
                                    row.inHouse
                                      ? 'border-emerald-300 text-emerald-800'
                                      : 'border-border text-muted-foreground/40'
                                  }`}
                                >
                                  in-house
                                </Badge>
                                <Badge
                                  variant='outline'
                                  className={`w-28 justify-center ${
                                    row.subContracted
                                      ? 'border-amber-300 text-amber-800'
                                      : 'border-border text-muted-foreground/40'
                                  }`}
                                >
                                  subcontracted
                                </Badge>
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </ProfileSection>
  )
}
